import type {
  CommandAction, CommandContext, CommandEvent, CommandRegistryApi, CommandResolution, CommandResolver, CommandResult,
} from "./commands.types";
import { ruleResolver } from "./ruleResolver";

export type CommandRegistryStore = Omit<CommandRegistryApi, "ui">;
export interface ConfirmCopy { title: string; body: string }
export interface CommandRegistryStoreOptions {
  /** Asks the person; `false` cancels the run. No asker: every confirmation cancels. */
  confirm?: (copy: ConfirmCopy) => Promise<boolean>;
  resolvers?: CommandResolver[];
}

const failed = (action: CommandAction): CommandResult => ({ status: "failed", message: `Couldn't run ${action.label}.` });
const isChatLike = (ctx: CommandContext): boolean => ctx.source === "chat" || ctx.source === "voice";

export const createCommandRegistryStore = (options: CommandRegistryStoreOptions = {}): CommandRegistryStore => {
  const sources: { read: () => CommandAction[] }[] = [];
  const listeners = new Set<(event: CommandEvent) => void>();
  const resolvers = options.resolvers ?? [ruleResolver];
  const confirm = options.confirm ?? (async () => false);

  const list = (): CommandAction[] => {
    const byId = new Map<string, CommandAction>();
    sources.forEach((source) => source.read().forEach((action) => byId.set(action.id, action)));
    return Array.from(byId.values());
  };
  const emit = (event: CommandEvent): void => {
    listeners.forEach((listener) => {
      try { listener(event); } catch { /* a listener never breaks a run */ }
    });
  };
  const safeAvailability = (action: CommandAction, ctx: CommandContext) => {
    try { return action.isAvailable(ctx); } catch { return { status: "hidden" } as const; }
  };

  return {
    register: (read) => {
      const entry = { read };
      sources.push(entry);
      return () => {
        const at = sources.indexOf(entry);
        if (at >= 0) sources.splice(at, 1);
      };
    },
    list,
    get: (id) => list().find((action) => action.id === id),
    run: async (id, args, ctx) => {
      const action = list().find((candidate) => candidate.id === id);
      if (!action) return { status: "blocked", message: "" };
      try {
        const availability = safeAvailability(action, ctx);
        if (availability.status === "hidden") return failed(action);
        if (availability.status === "soon") return { status: "blocked", message: `${action.label} isn't available yet.` };
        if (availability.status === "blocked") return { status: "blocked", message: availability.reason };
        const copy = action.confirm?.(args)
          ?? (action.destructive ? { title: `${action.label}?`, body: "This can't be undone." } : null);
        let result: CommandResult;
        if (copy && !(await confirm(copy))) result = { status: "cancelled", message: "Cancelled." };
        else result = await action.run(args, ctx);
        emit({ kind: "ran", action, args, ctx, result, at: Date.now() });
        return result;
      } catch {
        const result = failed(action);
        emit({ kind: "ran", action, args, ctx, result, at: Date.now() });
        return result;
      }
    },
    resolve: async (text, ctx): Promise<CommandResolution> => {
      const actions = list().filter((action) => !(isChatLike(ctx) && action.paletteOnly)
        && safeAvailability(action, ctx).status !== "hidden");
      for (const resolver of resolvers) {
        try {
          const resolution = await resolver.resolve(text, actions, ctx);
          if (resolution.kind !== "none") return resolution;
        } catch { /* a resolver that throws counts as no match */ }
      }
      return { kind: "none" };
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  };
};
